import { useDraggable } from '@dnd-kit/core';
import { cn } from '../../lib/cn';
import { BLOCCHI_DISPONIBILI, PREFISSO_PALETTE, type VoceBlocco } from './blocchiDisponibili';
import type { TipoBlocco } from '@email/types.ts';

interface BlockPaletteProps {
  /** Aggiunge il blocco in fondo: e' quello che succede a chi clicca invece
   *  di trascinare. */
  onAggiungi: (tipo: TipoBlocco) => void;
}

function VocePalette({ voce, onAggiungi }: { voce: VoceBlocco; onAggiungi: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${PREFISSO_PALETTE}${voce.tipo}`,
    data: { tipo: voce.tipo },
  });
  const Icona = voce.icona;

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onAggiungi}
      title={`${voce.descrizione}. Trascina nella email, oppure clicca per metterlo in fondo`}
      className={cn(
        // Stretto: una pastiglia in fila con le altre, per non mangiarsi
        // l'altezza che serve alla email. Largo: una scheda con la
        // descrizione, incolonnata di fianco alla tela.
        'flex items-center gap-2 px-2.5 py-2 rounded-lg border bg-white text-left',
        'lg:w-full lg:gap-2.5',
        'cursor-grab active:cursor-grabbing touch-none transition-colors',
        isDragging
          ? 'border-primary-400 opacity-40'
          : 'border-gray-200 hover:border-primary-400 hover:bg-primary-50/40'
      )}
      {...attributes}
      {...listeners}
    >
      <span className="flex-shrink-0 w-6 h-6 lg:w-7 lg:h-7 rounded-md bg-gray-100 flex items-center justify-center">
        <Icona className="w-3.5 h-3.5 text-neutral-500" />
      </span>
      <span className="min-w-0">
        <span className="block text-xs font-semibold text-gray-900">{voce.etichetta}</span>
        <span className="hidden lg:block text-[11px] text-neutral-400 truncate">
          {voce.descrizione}
        </span>
      </span>
    </button>
  );
}

/**
 * Il pannello dei blocchi: si prende un elemento e lo si lascia cadere nel
 * punto della email dove deve stare.
 *
 * Il clic continua a funzionare e mette il blocco in fondo. Non e' un ripiego:
 * il trascinamento non si fa con la tastiera, e senza il clic chi naviga cosi'
 * resterebbe fuori.
 */
export function BlockPalette({ onAggiungi }: BlockPaletteProps) {
  return (
    <div className="lg:sticky lg:top-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-2 px-0.5">
        Blocchi
      </p>
      <div className="flex flex-wrap gap-1.5 lg:grid lg:grid-cols-1">
        {BLOCCHI_DISPONIBILI.map((voce) => (
          <VocePalette key={voce.tipo} voce={voce} onAggiungi={() => onAggiungi(voce.tipo)} />
        ))}
      </div>
      <p className="hidden lg:block text-[11px] text-neutral-400 mt-2.5 leading-relaxed px-0.5">
        Trascina un blocco nel punto in cui lo vuoi, oppure clicca per metterlo in fondo.
      </p>
    </div>
  );
}
